import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { Settings, Clock, Zap, Save, Download, Upload, FileJson, Trash2, AlertTriangle, Github, Cloud, LogOut, RefreshCw, Mail, BookOpen, Rocket, Eye, CalendarClock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CalendarSettings } from '@/components/CalendarSettings'
import type { ExportData } from '@trackmind/storage'
import { cn } from '@/lib/utils'
import { deliverFile } from '@/lib/file-delivery'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import type { UserPreferences, EnergyLevel } from '@trackmind/core'
import { orphanedTasks, todayIn } from '@trackmind/core'
import { AssistantConnect } from '@/components/AssistantConnect'
import { TimezoneSetting } from '@/components/TimezoneSetting'
import { useApp } from '@/context/AppContext'
import { useHelpMode } from '@/context/HelpModeContext'
import { usePWAUpdate } from '@/context/PWAUpdateContext'
const Documentation = lazy(() => import('@/components/Documentation').then((m) => ({ default: m.Documentation })))

interface DayWorkHours {
  start: string
  end: string
}

interface EnergyBlock {
  start: string
  end: string
  level: EnergyLevel
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const DAY_LABELS: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
}

interface SettingsDialogProps {
  onRestartWizard?: () => void
}

export function SettingsDialog({ onRestartWizard }: SettingsDialogProps) {
  const {
    preferences: storedPreferences,
    savePreferences,
    tasks,
    projects,
    lists,
    exportData,
    importData,
    clearAllData,
    deleteTask,
    recomputeStreak,
    // Auth & Sync
    authProvider,
    authState,
    isGitHubAvailable,
    isGoogleAvailable,
    isGitHubConnected,
    isGoogleConnected,
    switchProvider,
    loginWithGitHub,
    loginWithGoogle,
    logout,
    syncStatus,
    lastSyncTime,
    pendingChanges,
    sync,
    forcePush,
    forcePull,
  } = useApp()

  const { isHelpModeActive, toggleHelpMode } = useHelpMode()
  const { needRefresh, updateNow, checkForUpdate, checkResult } = usePWAUpdate()

  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [selectedDay, setSelectedDay] = useState('monday')
  const [activeTab, setActiveTab] = useState('sync')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Export/Import state
  const [switching, setSwitching] = useState(false)
  const [switchError, setSwitchError] = useState<string | null>(null)
  const [switchResult, setSwitchResult] = useState<string | null>(null)

  const [exporting, setExporting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const [importSuccess, setImportSuccess] = useState(false)
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [clearingOrphans, setClearingOrphans] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)

  // Sync handlers with error display
  const handleSync = async () => {
    setSyncError(null)
    try {
      const result = await sync()
      if (result && !result.success) {
        setSyncError(result.message || 'Sync failed')
        console.error('[Sync] Error:', result.message)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown sync error'
      setSyncError(message)
      console.error('[Sync] Exception:', error)
    }
  }

  const handleForcePush = async () => {
    setSyncError(null)
    try {
      const result = await forcePush()
      if (result && !result.success) {
        setSyncError(result.message || 'Force push failed')
        console.error('[Sync] Force push error:', result.message)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      setSyncError(message)
      console.error('[Sync] Force push exception:', error)
    }
  }

  const handleForcePull = async () => {
    setSyncError(null)
    try {
      const result = await forcePull()
      if (result && !result.success) {
        setSyncError(result.message || 'Force pull failed')
        console.error('[Sync] Force pull error:', result.message)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      setSyncError(message)
      console.error('[Sync] Force pull exception:', error)
    }
  }

  useEffect(() => {
    if (open && storedPreferences) {
      setPreferences(storedPreferences)
    }
  }, [open, storedPreferences])

  const handleClearOrphans = async () => {
    const stranded = orphanedTasks(projects, tasks)
    if (stranded.length === 0) return
    const ok = window.confirm(
      `Delete ${stranded.length} task${stranded.length === 1 ? '' : 's'} left behind by `
      + 'a deleted project? This cannot be undone — export a backup first if '
      + 'you want to keep a copy.'
    )
    if (!ok) return
    setClearingOrphans(true)
    try {
      for (const task of stranded) await deleteTask(task.id)
    } catch (error) {
      console.error('Failed to clear orphaned tasks:', error)
    } finally {
      setClearingOrphans(false)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const data = await exportData()
      if (data) {
        // Same delivery path as every other export: the share sheet on a
        // phone (where a blob download is unreliable, and unusable inside an
        // installed PWA), a download everywhere else, and never twice.
        await deliverFile({
          filename: `trackmind-export-${todayIn(preferences?.timezone)}.json`,
          parts: [JSON.stringify(data, null, 2)],
          type: 'application/json',
        })
      }
    } catch (error) {
      console.error('Failed to export data:', error)
    } finally {
      setExporting(false)
    }
  }

  const handleImportClick = () => {
    setImportError(null)
    setImportSuccess(false)
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setImporting(true)
    setImportError(null)
    setImportSuccess(false)

    try {
      const text = await file.text()
      const data = JSON.parse(text)

      // Validate basic structure
      if (!data.version) {
        // Try to convert from old server export format
        // Legacy server-format file: shape is only known at runtime, so the
        // cast is deliberate and importData does the validating.
        const converted = convertOldExportFormat(data) as unknown as ExportData
        await importData(converted)
      } else {
        await importData(data)
      }

      setImportSuccess(true)
    } catch (error) {
      console.error('Failed to import data:', error)
      setImportError(error instanceof Error ? error.message : 'Invalid JSON file')
    } finally {
      setImporting(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  // Convert old FastAPI server export format to new format
  const convertOldExportFormat = (oldData: Record<string, unknown>) => {
    const now = new Date().toISOString()

    // Helper to convert tasks from old format
    const convertTasks = (oldTasks: Record<string, unknown>[] = [], habits: Record<string, unknown>[] = []) => {
      const allTasks = [...oldTasks, ...habits.map(h => ({ ...h, is_habit: true }))]
      return allTasks.map((t: Record<string, unknown>) => ({
        id: t.id as string,
        project_id: (t.project_id as string) || 'default',
        title: t.title as string,
        description: t.description as string || '',
        status: t.status as string || 'todo',
        priority: t.priority as number || 2,
        difficulty: t.difficulty as number || 2,
        energy_level: t.energy_level as string || 'medium',
        estimated_minutes: t.estimated_minutes as number || 30,
        deadline: t.deadline as string || null,
        tags: t.tags as string[] || [],
        dependencies: t.dependencies as string[] || [],
        is_habit: !!t.is_habit,
        recurrence: t.recurrence || null,
        is_paused: !!t.is_paused,
        paused_until: t.paused_until as string || null,
        parent_task_id: t.parent_task_id as string || null,
        created_at: now,
        updated_at: now,
      }))
    }

    // Helper to convert projects
    const convertProjects = (oldProjects: Record<string, unknown>[] = []) => {
      return oldProjects.map((p: Record<string, unknown>) => ({
        id: p.id as string,
        name: p.name as string,
        description: p.description as string || '',
        status: p.status as string || 'active',
        created_at: (p.created_at as string) || now,
        updated_at: now,
      }))
    }

    // Helper to convert lists
    const convertLists = (oldLists: Record<string, unknown>[] = []) => {
      return oldLists.map((l: Record<string, unknown>) => ({
        id: l.id as string,
        name: l.name as string,
        description: l.description as string || '',
        list_type: l.list_type as string || 'checklist',
        items: (l.items as Record<string, unknown>[] || []).map((item: Record<string, unknown>) => ({
          id: item.id as string,
          text: item.text as string,
          completed_at: item.completed_at as string || null,
          quantity: item.quantity as number || null,
          notes: item.notes as string || '',
          category: item.category as string || null,
          url: item.url as string || null,
          tags: item.tags as string[] || [],
          order: item.order as number || 0,
          price: item.price as number || null,
          rating: item.rating as number || null,
          priority: item.priority as string || null,
          location: item.location as string || null,
          due_date: item.due_date as string || null,
          source: item.source as string || null,
        })),
        categories: l.categories as string[] || [],
        is_pinned: !!l.is_pinned,
        is_archived: false,
        color: l.color as string || null,
        icon: l.icon as string || null,
        default_sort: l.default_sort as string || 'order',
        linked_habit_ids: l.linked_habit_ids as string[] || [],
        linked_project_ids: l.linked_project_ids as string[] || [],
        created_at: (l.created_at as string) || now,
        updated_at: now,
      }))
    }

    return {
      version: '1.0',
      exportedAt: now,
      tasks: convertTasks(
        oldData.tasks as Record<string, unknown>[],
        oldData.habits as Record<string, unknown>[]
      ),
      projects: convertProjects(oldData.projects as Record<string, unknown>[]),
      schedules: [],
      goals: [],
      notes: [],
      lists: convertLists(oldData.lists as Record<string, unknown>[]),
      listInstances: [],
      habitInstances: [],
      preferences: storedPreferences,
      stats: { total_xp: 0, level: 1, tasks_completed: 0, streaks: {} },
    }
  }

  const handleClearAll = async () => {
    setClearing(true)
    try {
      await clearAllData()
      setShowClearConfirm(false)
    } catch (error) {
      console.error('Failed to clear data:', error)
    } finally {
      setClearing(false)
    }
  }

  const handleSave = async () => {
    if (!preferences) return
    setSaving(true)
    try {
      await savePreferences(preferences)
      setOpen(false)
    } catch (error) {
      console.error('Failed to save preferences:', error)
    } finally {
      setSaving(false)
    }
  }

  const updateWorkHours = (day: string, field: 'start' | 'end', value: string) => {
    if (!preferences) return
    setPreferences({
      ...preferences,
      work_hours: {
        ...preferences.work_hours,
        [day]: {
          ...preferences.work_hours[day],
          [field]: value,
        },
      },
    })
  }

  const updateEnergyBlock = (day: string, index: number, field: keyof EnergyBlock, value: string) => {
    if (!preferences) return
    const patterns = [...preferences.energy_patterns[day]]
    patterns[index] = { ...patterns[index], [field]: value }
    setPreferences({
      ...preferences,
      energy_patterns: {
        ...preferences.energy_patterns,
        [day]: patterns,
      },
    })
  }

  const addEnergyBlock = (day: string) => {
    if (!preferences) return
    const patterns = preferences.energy_patterns[day] || []
    const lastBlock = patterns[patterns.length - 1]
    const newBlock: EnergyBlock = {
      start: lastBlock?.end || '08:00',
      end: '22:00',
      level: 'medium',
    }
    setPreferences({
      ...preferences,
      energy_patterns: {
        ...preferences.energy_patterns,
        [day]: [...patterns, newBlock],
      },
    })
  }

  const removeEnergyBlock = (day: string, index: number) => {
    if (!preferences) return
    const patterns = preferences.energy_patterns[day].filter((_, i) => i !== index)
    setPreferences({
      ...preferences,
      energy_patterns: {
        ...preferences.energy_patterns,
        [day]: patterns,
      },
    })
  }

  const copyToAllDays = (sourceDay: string) => {
    if (!preferences) return
    const sourceWorkHours = preferences.work_hours[sourceDay]
    const sourceEnergy = preferences.energy_patterns[sourceDay]

    const newWorkHours: Record<string, DayWorkHours> = {}
    const newEnergyPatterns: Record<string, EnergyBlock[]> = {}

    for (const day of DAYS) {
      newWorkHours[day] = { ...sourceWorkHours }
      newEnergyPatterns[day] = sourceEnergy.map(e => ({ ...e }))
    }

    setPreferences({
      ...preferences,
      work_hours: newWorkHours,
      energy_patterns: newEnergyPatterns,
    })
  }

  // Calculate summary
  const habitCount = tasks.filter(t => t.is_habit).length
  const taskCount = tasks.filter(t => !t.is_habit).length

  /**
   * Tasks pointing at a project that no longer exists.
   *
   * Deleting a project used to remove only the project row, leaving its tasks
   * behind with a dangling `project_id`. They show up nowhere under Projects
   * but are still counted by Analytics, so the numbers there read high with
   * no way to see why. v1.58 stopped creating them; this clears out the ones
   * already in the data. Empty for anyone who never hit the old behaviour,
   * and the card stays hidden in that case.
   */
  const orphans = orphanedTasks(projects, tasks)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Settings" data-help="settings">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Settings
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure your work hours, energy patterns, cloud sync, and data management
          </DialogDescription>
        </DialogHeader>

        {preferences ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            {/* Six tabs don't fit at 390px with labels, and a horizontally
                scrolling tab strip is easy to miss — so phones get icons and
                desktop keeps the words. */}
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="sync" className="gap-2" title="Sync">
                <Cloud className="h-4 w-4" />
                <span className="hidden md:inline">Sync</span>
              </TabsTrigger>
              <TabsTrigger value="calendar" className="gap-2" title="Calendar">
                <CalendarClock className="h-4 w-4" />
                <span className="hidden md:inline">Calendar</span>
              </TabsTrigger>
              <TabsTrigger value="work-hours" className="gap-2" title="Work Hours">
                <Clock className="h-4 w-4" />
                <span className="hidden md:inline">Work Hours</span>
              </TabsTrigger>
              <TabsTrigger value="energy" className="gap-2" title="Energy">
                <Zap className="h-4 w-4" />
                <span className="hidden md:inline">Energy</span>
              </TabsTrigger>
              <TabsTrigger value="data" className="gap-2" title="Data">
                <FileJson className="h-4 w-4" />
                <span className="hidden md:inline">Data</span>
              </TabsTrigger>
              <TabsTrigger value="help" className="gap-2" title="Help">
                <BookOpen className="h-4 w-4" />
                <span className="hidden md:inline">Help</span>
              </TabsTrigger>
            </TabsList>

            {/* Calendar Tab */}
            <TabsContent value="calendar" className="space-y-4">
              <CalendarSettings />
            </TabsContent>

            {/* Day Selector (only for work hours and energy tabs) */}
            {(activeTab === 'work-hours' || activeTab === 'energy') && (
              <div className="flex gap-1 justify-center">
                {DAYS.map((day) => (
                  <Button
                    key={day}
                    variant={selectedDay === day ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedDay(day)}
                    className="px-2"
                  >
                    {DAY_LABELS[day]}
                  </Button>
                ))}
              </div>
            )}

            {/* Work Hours Tab */}
            <TabsContent value="work-hours" className="space-y-4">
              {/* Lives beside work hours because it is the same question —
                  when your day starts and ends — and because a seventh tab
                  would break the six-column grid on a phone. */}
              <Card>
                <CardContent className="pt-6">
                  <TimezoneSetting
                    preferences={preferences}
                    onChange={setPreferences}
                  />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base capitalize">{selectedDay} Work Hours</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Input
                        type="time"
                        value={preferences.work_hours[selectedDay]?.start || '08:00'}
                        onChange={(e) => updateWorkHours(selectedDay, 'start', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Time</Label>
                      <Input
                        type="time"
                        value={preferences.work_hours[selectedDay]?.end || '22:00'}
                        onChange={(e) => updateWorkHours(selectedDay, 'end', e.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToAllDays(selectedDay)}
                  >
                    Copy to all days
                  </Button>
                </CardContent>
              </Card>

              {/* Scheduling Settings */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Scheduling Settings</CardTitle>
                  <CardDescription>
                    Configure how tasks are scheduled
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Max Continuous Work Time (minutes)</Label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="number"
                        min={30}
                        max={480}
                        step={15}
                        value={preferences.scheduling_preferences?.max_deep_work_stretch ?? 120}
                        onChange={(e) => {
                          const value = Math.max(30, Math.min(480, parseInt(e.target.value) || 120))
                          setPreferences({
                            ...preferences,
                            scheduling_preferences: {
                              ...preferences.scheduling_preferences,
                              max_deep_work_stretch: value,
                            },
                          })
                        }}
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">
                        = {Math.floor((preferences.scheduling_preferences?.max_deep_work_stretch ?? 120) / 60)}h {(preferences.scheduling_preferences?.max_deep_work_stretch ?? 120) % 60}m
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      After this duration, a 15-minute break is automatically inserted. Tasks longer than this won't be scheduled.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Minimum Task Duration (minutes)</Label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="number"
                        min={5}
                        max={60}
                        step={5}
                        value={preferences.task_defaults?.min_duration ?? 15}
                        onChange={(e) => {
                          const value = Math.max(5, Math.min(60, parseInt(e.target.value) || 15))
                          setPreferences({
                            ...preferences,
                            task_defaults: {
                              ...preferences.task_defaults,
                              min_duration: value,
                            },
                          })
                        }}
                        className="w-24"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Tasks shorter than this won't be scheduled. They'll appear in the unscheduled list.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Energy Patterns Tab */}
            <TabsContent value="energy" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base capitalize">{selectedDay} Energy Patterns</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {preferences.energy_patterns[selectedDay]?.map((block, index) => (
                    <div key={index} className="flex items-end gap-2 p-3 border rounded-lg">
                      <div className="flex-1 space-y-2">
                        <Label className="text-xs">Start</Label>
                        <Input
                          type="time"
                          value={block.start}
                          onChange={(e) => updateEnergyBlock(selectedDay, index, 'start', e.target.value)}
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <Label className="text-xs">End</Label>
                        <Input
                          type="time"
                          value={block.end}
                          onChange={(e) => updateEnergyBlock(selectedDay, index, 'end', e.target.value)}
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <Label className="text-xs">Energy</Label>
                        <Select
                          value={block.level}
                          onValueChange={(value) => updateEnergyBlock(selectedDay, index, 'level', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {preferences.energy_patterns[selectedDay].length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEnergyBlock(selectedDay, index)}
                          className="text-destructive"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addEnergyBlock(selectedDay)}
                    >
                      Add Block
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToAllDays(selectedDay)}
                    >
                      Copy to all days
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Sync Tab */}
            <TabsContent value="sync" className="space-y-4">
              {/* Connected Account */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Cloud className="h-4 w-4" />
                    Cloud Sync
                  </CardTitle>
                  <CardDescription>
                    Connect to sync your data across devices
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {authState?.isAuthenticated ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                        {authState.user?.avatarUrl && (
                          <img
                            src={authState.user.avatarUrl}
                            alt={authState.user.displayName || 'User'}
                            className="h-10 w-10 rounded-full"
                          />
                        )}
                        <div className="flex-1">
                          <div className="font-medium">{authState.user?.displayName?.split(' ')[0] || authState.user?.email?.split('@')[0]}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            {authProvider === 'google' ? (
                              <>
                                <Mail className="h-3 w-3" />
                                {authState.user?.email}
                              </>
                            ) : (
                              <>
                                <Github className="h-3 w-3" />
                                @{authState.user?.id}
                              </>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Syncing with {authProvider === 'google' ? 'Google Drive' : 'GitHub'}
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={logout}>
                          <LogOut className="h-4 w-4 mr-2" />
                          Sign Out
                        </Button>
                      </div>

                      {/* Move to the other cloud provider. Does what
                          logout→login→sync can't: carries the finance files,
                          which live only in the cloud and never touch the
                          local database. */}
                      <div className="border-t pt-3 space-y-2">
                        <p className="text-sm font-medium">Switch cloud provider</p>
                        <p className="text-xs text-muted-foreground">
                          Copies everything — including your finance files — to{' '}
                          {authProvider === 'google' ? 'GitHub' : 'Google Drive'}. Your data
                          stays in {authProvider === 'google' ? 'Google Drive' : 'GitHub'} too,
                          as a fallback.
                        </p>
                        {switchError && (
                          <p className="text-xs text-destructive">{switchError}</p>
                        )}
                        {switchResult && (
                          <p className="text-xs text-green-600 dark:text-green-400">{switchResult}</p>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          disabled={switching}
                          onClick={async () => {
                            const target = authProvider === 'google' ? 'github' : 'google';
                            setSwitchError(null);
                            setSwitchResult(null);

                            // The target provider must be connected first —
                            // we copy while BOTH sessions are alive.
                            const targetReady = target === 'github'
                              ? isGitHubAvailable && isGitHubConnected
                              : isGoogleAvailable && isGoogleConnected;
                            if (!targetReady) {
                              setSwitchError(
                                `Sign in with ${target === 'github' ? 'GitHub' : 'Google'} first — ` +
                                'both connections are needed to copy your data across.'
                              );
                              if (target === 'github') loginWithGitHub(); else loginWithGoogle();
                              return;
                            }

                            setSwitching(true);
                            try {
                              const res = await switchProvider(target);
                              setSwitchResult(
                                `Moved to ${target === 'github' ? 'GitHub' : 'Google Drive'}` +
                                (res && res.financeFilesMoved > 0
                                  ? ` · ${res.financeFilesMoved} finance file${res.financeFilesMoved === 1 ? '' : 's'} copied`
                                  : '')
                              );
                            } catch (error) {
                              setSwitchError(error instanceof Error ? error.message : 'Switch failed');
                            } finally {
                              setSwitching(false);
                            }
                          }}
                        >
                          {switching
                            ? 'Moving your data…'
                            : `Switch to ${authProvider === 'google' ? 'GitHub' : 'Google Drive'}`}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Sign in to sync your tasks, projects, and settings across all your devices.
                        Your data lives in your own account — a private GitHub repository, or a
                        TrackMind folder in your Google Drive. Same features either way.
                      </p>

                      {/* GitHub Sign-in */}
                      {isGitHubAvailable ? (
                        <div className="space-y-2">
                          <Button onClick={loginWithGitHub} className="w-full" variant="default">
                            <Github className="h-4 w-4 mr-2" />
                            Sign in with GitHub
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Button disabled className="w-full" variant="outline">
                            <Github className="h-4 w-4 mr-2 opacity-50" />
                            GitHub Sign-in Not Configured
                          </Button>
                          <p className="text-xs text-muted-foreground text-center">
                            GitHub OAuth is not set up for this deployment
                          </p>
                        </div>
                      )}

                      {/* Google Sign-in */}
                      {isGoogleAvailable ? (
                        <div className="space-y-2 pt-2">
                          <Button onClick={loginWithGoogle} className="w-full" variant="outline">
                            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            Sign in with Google
                          </Button>
                          <p className="text-xs text-muted-foreground text-center">
                            Data is stored in a visible "TrackMind" folder in your Google Drive.
                            This doesn't read your calendar — that's a separate permission you
                            can add in one tap afterwards, from the Calendar tab.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2 pt-2">
                          <Button disabled className="w-full" variant="outline">
                            Google Sign-in Not Configured
                          </Button>
                          <p className="text-xs text-muted-foreground text-center">
                            Google OAuth is not set up for this deployment
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sync Status */}
              {authState?.isAuthenticated && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <RefreshCw className="h-4 w-4" />
                      Sync Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 p-3 bg-muted/50 rounded-lg text-sm">
                      <div>
                        <div className="text-muted-foreground">Status</div>
                        <div className="font-medium capitalize">{syncStatus}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Pending Changes</div>
                        <div className="font-medium">{pendingChanges}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-muted-foreground">Last Synced</div>
                        <div className="font-medium">
                          {lastSyncTime ? lastSyncTime.toLocaleString() : 'Never'}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSync}
                        disabled={syncStatus === 'syncing'}
                        className="flex-1"
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
                        Sync
                      </Button>
                    </div>
                    {syncError && (
                      <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-md">
                        <p className="text-sm text-red-600 dark:text-red-400">{syncError}</p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        onClick={handleForcePush}
                        disabled={syncStatus === 'syncing'}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        Force Push
                      </Button>
                      <Button
                        onClick={handleForcePull}
                        disabled={syncStatus === 'syncing'}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        Force Pull
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Force Push overwrites remote data with local. Force Pull overwrites local data with remote.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* The MCP connector. It lives under Sync because it is the same
                  question — what else reaches your data — and because a
                  seventh tab would break the six-column grid on phones. */}
              <AssistantConnect />
            </TabsContent>

            {/* Data Tab (Export/Import) */}
            <TabsContent value="data" className="space-y-4">
              {/* Summary */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Your Data</CardTitle>
                  <CardDescription>Data stored locally in your browser</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 p-3 bg-muted/50 rounded-lg text-sm">
                    <div className="text-center">
                      <div className="text-lg font-bold">{projects.length}</div>
                      <div className="text-muted-foreground">Projects</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold">{taskCount}</div>
                      <div className="text-muted-foreground">Tasks</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold">{habitCount}</div>
                      <div className="text-muted-foreground">Habits</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold">{lists.length}</div>
                      <div className="text-muted-foreground">Lists</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Left-behind tasks. Hidden entirely when there are none, which
                  is everyone who never deleted a project before v1.58. */}
              {orphans.length > 0 && (
                <Card className="border-amber-500/40">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      {orphans.length} task{orphans.length === 1 ? '' : 's'} without a project
                    </CardTitle>
                    <CardDescription>
                      These belonged to a project that was deleted before the app
                      removed a project's tasks along with it. They don't appear
                      under any project, but they still count in Analytics.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <ul className="text-sm text-muted-foreground space-y-1 max-h-32 overflow-y-auto">
                      {orphans.slice(0, 8).map((t) => (
                        <li key={t.id} className="truncate">• {t.title}</li>
                      ))}
                      {orphans.length > 8 && (
                        <li className="italic">…and {orphans.length - 8} more</li>
                      )}
                    </ul>
                    <Button
                      onClick={handleClearOrphans}
                      disabled={clearingOrphans}
                      variant="outline"
                      className="w-full"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {clearingOrphans ? 'Deleting…' : `Delete ${orphans.length} orphaned task${orphans.length === 1 ? '' : 's'}`}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Export */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Export Data
                  </CardTitle>
                  <CardDescription>
                    Download all your data as a JSON file for backup
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleExport} disabled={exporting} className="w-full">
                    <Download className="h-4 w-4 mr-2" />
                    {exporting ? 'Exporting...' : 'Download JSON Export'}
                  </Button>
                </CardContent>
              </Card>

              {/* Import */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    Import Data
                  </CardTitle>
                  <CardDescription>
                    Import data from a JSON export file. This also supports exports from older versions.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".json"
                    className="hidden"
                  />
                  <Button
                    onClick={handleImportClick}
                    disabled={importing}
                    variant="outline"
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {importing ? 'Importing...' : 'Select JSON File to Import'}
                  </Button>
                  {importError && (
                    <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
                      Error: {importError}
                    </div>
                  )}
                  {importSuccess && (
                    <div className="text-sm text-green-600 bg-green-100 dark:bg-green-900/20 p-2 rounded">
                      Data imported successfully!
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Clear Data */}
              <Card className="border-destructive/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-destructive">
                    <Trash2 className="h-4 w-4" />
                    Clear All Data
                  </CardTitle>
                  <CardDescription>
                    Permanently delete all local data. This cannot be undone.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!showClearConfirm ? (
                    <Button
                      onClick={() => setShowClearConfirm(true)}
                      variant="destructive"
                      className="w-full"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Clear All Data
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg text-sm">
                        <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                        <p>
                          Are you sure? This will delete all {taskCount + habitCount} tasks, {projects.length} projects,
                          and {lists.length} lists. This action cannot be undone.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleClearAll}
                          disabled={clearing}
                          variant="destructive"
                          className="flex-1"
                        >
                          {clearing ? 'Clearing...' : 'Yes, Delete Everything'}
                        </Button>
                        <Button
                          onClick={() => setShowClearConfirm(false)}
                          variant="outline"
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Help Tab */}
            <TabsContent value="help" className="space-y-4">
              {/* Documentation */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Documentation
                  </CardTitle>
                  <CardDescription>
                    Learn how to use TrackMind effectively
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Suspense
                    fallback={
                      <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
                    }
                  >
                    <Documentation />
                  </Suspense>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Restart Wizard */}
                  {onRestartWizard && (
                    <Button
                      onClick={() => {
                        setOpen(false)
                        onRestartWizard()
                      }}
                      variant="outline"
                      className="w-full justify-start"
                    >
                      <Rocket className="h-4 w-4 mr-2" />
                      Restart Setup Wizard
                    </Button>
                  )}

                  {/* Toggle Help Mode */}
                  <Button
                    onClick={() => {
                      setOpen(false)
                      toggleHelpMode()
                    }}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {isHelpModeActive ? 'Exit Help Mode' : 'Enter Help Mode'}
                  </Button>

                  <p className="text-xs text-muted-foreground">
                    Help Mode highlights UI elements with explanations. You can also toggle it with{' '}
                    <kbd className="px-1 bg-muted rounded text-xs">Shift + ?</kbd> or the{' '}
                    <Eye className="inline h-3 w-3" /> button in the bottom-right corner.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>

            {/* Footer: version + update + maintenance actions */}
            <div className="pt-3 border-t space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
                <span className="text-muted-foreground">
                  TrackMind <span className="font-mono">v{__APP_VERSION__}</span>
                  {/*
                    The release DATE, not the date this device installed it.
                    "v1.93.0" alone gives no sense of whether a build is fresh
                    or eight months stale, which is the actual question when
                    something looks wrong. Falls back to nothing rather than
                    guessing if the changelog has not caught up.
                  */}
                  {__APP_RELEASE_DATE__ && (
                    <> · <span title="Release date">{__APP_RELEASE_DATE__}</span></>
                  )}
                  {' · '}
                  {/* Real pages, not routes — they must stay reachable even if
                      the app bundle fails, and Google's OAuth review checks
                      the policy is publicly available. */}
                  <a
                    href="/privacy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-foreground"
                  >
                    Privacy
                  </a>
                  {' · '}
                  <a
                    href="/terms/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-foreground"
                  >
                    Terms
                  </a>
                </span>
                {needRefresh ? (
                  <Button size="sm" className="h-7 gap-1.5 text-xs" onClick={updateNow}>
                    <RefreshCw className="h-3.5 w-3.5" />
                    Update &amp; reload
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 text-xs"
                    onClick={checkForUpdate}
                    disabled={checkResult === 'checking'}
                  >
                    <RefreshCw className={cn('h-3.5 w-3.5', checkResult === 'checking' && 'animate-spin')} />
                    {checkResult === 'checking'
                      ? 'Checking…'
                      : checkResult === 'up-to-date'
                        ? 'Up to date'
                        : checkResult === 'unsupported'
                          ? 'Updates n/a'
                          : 'Check for updates'}
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {checkResult === 'up-to-date' && "You're on the latest version."}
                  {checkResult === 'unsupported' && 'Update checks run in the installed app / production site.'}
                </span>
                <button
                  onClick={async () => {
                    try {
                      await recomputeStreak()
                    } catch (err) {
                      console.error('Recompute streak failed:', err)
                    }
                  }}
                  className="underline hover:text-foreground shrink-0"
                  title="Rebuild current/longest streak from your actual completion history"
                >
                  Recompute streak
                </button>
              </div>
            </div>
          </Tabs>
        ) : (
          <div className="py-8 text-center text-muted-foreground">Loading settings...</div>
        )}
      </DialogContent>
    </Dialog>
  )
}
